import { type FC, useCallback, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useCompleteContractMutation,
  useLazyOpenChestQuery,
} from '@api/queries/contract';
import { ModalContext } from '@components/Modals/Modal/config';
import { StringSmall, TextXSmall } from '@components/Typography/Typography';
import { MotionPicture, Picture } from '@components/ui/Picture/Picture';
import { useAppDispatch, useAppSelector } from '@redux/store';
import cn from 'classnames';
import {
  animate,
  type AnimationPlaybackControls,
  motion,
  useMotionValue,
  useTransform,
} from 'framer-motion';

import { useSelectorRef } from '@/common/hooks/selector-ref';
import { CompleteContractAnimationContext } from '@/components/Animations/CompleteContractAnimation/context';
import { setContractList } from '@/redux/reducers/contractReducer';
// import { setContractList } from '@/redux/reducers/contractReducer';
import { setOpenChestIsActive } from '@/redux/reducers/modalsReducer';

import css from './styles.module.scss';

import casePng from '/img/icons/case.png';
import caseWebp from '/img/icons/case.webp';
import effectPng from '/img/icons/chest-effect.png';
import effectWebp from '/img/icons/chest-effect.webp';
import gradientStar from '/img/icons/gradient-star.svg';
import starPng from '/img/icons/star-large.png';
import starWebp from '/img/icons/star-large.webp';

const ContractProgress: FC = () => {
  const { t } = useTranslation();
  const { animationStep, setAnimationStep, setContractId } = useContext(
    CompleteContractAnimationContext,
  );
  const rotate = useMotionValue(0);
  const [, completeContractApiState] = useCompleteContractMutation({
    fixedCacheKey: 'shared-complete-contract',
  });

  const { openModal } = useContext(ModalContext);
  const userPoints = useAppSelector((state) => state.contract.userPoints);
  const userPointsRef = useSelectorRef((state) => state.contract.userPoints);
  const [openChest] = useLazyOpenChestQuery();
  const pointsForNewChest = useAppSelector(
    (state) => state.contract.chestBoxRequiredPoints,
  );
  const pointsForNewChestRef = useSelectorRef(
    (state) => state.contract.chestBoxRequiredPoints,
  );
  const availableChestsCount = useAppSelector(
    (state) => state.contract.availableChestsCount,
  );
  const x = useMotionValue((userPoints || 0) % (pointsForNewChest || 0));
  const width = useTransform(x, [0, pointsForNewChest || 0], ['0%', '100%']);
  const dispatch = useAppDispatch();
  const onClick = useCallback((): void => {
    if (availableChestsCount === 0) {
      openModal({ renderKind: 'chestInfo', props: undefined });
    } else {
      openChest()
        .unwrap()
        .then(() => {
          dispatch(setOpenChestIsActive(true));
        });
    }
  }, [availableChestsCount, openChest, openModal]);

  useEffect(() => {
    let controls1: AnimationPlaybackControls | undefined;
    let controls2: AnimationPlaybackControls | undefined;

    if (animationStep === 3) {
      const oldRotate = rotate.get();
      controls1 = animate(rotate, oldRotate + 72, {
        duration: 0.5,
        ease: 'easeOut',
      });
      controls2 = animate(
        x,
        (userPointsRef.current || 0) % (pointsForNewChestRef.current || 0),
        {
          duration: 0.5,
          ease: 'easeOut',
        },
      );

      return (): void => {
        controls1?.stop();
        controls2?.stop();
      };
    }
  }, [animationStep]);

  return (
    <div className="flex items-center gap-4 px-4">
      <div className="flex flex-1 flex-col gap-1">
        <div className="flex w-full items-center justify-between">
          <StringSmall weight="bold">{t('Success Points')}</StringSmall>
          <StringSmall
            weight="bold"
            className="flex items-center gap-[0.125rem]"
          >
            <span>{(userPoints || 0) % (pointsForNewChest || 0)}</span>
            <span className="text-white/40">/</span>
            <span>{pointsForNewChest}</span>
          </StringSmall>
        </div>
        <div className={css.scale}>
          <motion.div
            style={{
              width,
            }}
            className={css.progress}
          >
            <div className="absolute w-[1.875rem] h-7 top-1/2 right-0 -translate-y-1/2 translate-x-1/2">
              <div className="relative w-full h-full">
                <MotionPicture
                  style={{ rotate }}
                  animate={{
                    filter:
                      animationStep === 3
                        ? 'drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25)) drop-shadow(0px 0px 4px #FFD600)'
                        : 'drop-shadow(0px 0px 0px rgba(0, 0, 0, 0)) drop-shadow(0px 0px 0px rgba(0, 0, 0, 0))',
                  }}
                  transition={{ duration: 0.5, ease: 'easeOut' }}
                  onAnimationComplete={() => {
                    if (completeContractApiState.data) {
                      dispatch(
                        setContractList(
                          completeContractApiState.data.contracts,
                        ),
                      );
                    }
                    setContractId(null);
                    setAnimationStep(1);
                  }}
                  img={gradientStar}
                  className={css.star}
                />
                {animationStep === 2 && (
                  <MotionPicture
                    layoutId="contract-complete-point"
                    exit={{
                      opacity: 0,
                      transition: { duration: 0.5, ease: 'easeOut' },
                    }}
                    className={css.star}
                    transition={{ layout: { duration: 0.6, ease: 'easeOut' } }}
                    img={starPng}
                    webp={starWebp}
                    alt="point"
                  />
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      <div
        onClick={onClick}
        className="relative cursor-pointer w-16 h-16 shrink-0"
      >
        <Picture
          className={cn('w-full h-full', css.case)}
          img={casePng}
          webp={caseWebp}
          alt="case"
        />
        {!!availableChestsCount && (
          <MotionPicture
            initial={{ opacity: 0, scale: 1.3, x: '-50%' }}
            animate={{ opacity: [null, 1, 0], scale: 1.3, x: '-50%' }}
            transition={{
              ease: 'linear',
              duration: 3,
              delay: 0.8,
              repeatDelay: 0.8,
              repeat: Infinity,
            }}
            className="absolute top-0 left-1/2 -translate-x-1/2 w-full aspect-[182/142] -z-[1]"
            img={effectPng}
            webp={effectWebp}
            alt="effect"
          />
        )}

        {!!availableChestsCount && (
          <div className={css.countWrapper}>
            <TextXSmall
              weight="bold"
              className="!visibletext-[0.625rem] !text-main-bg !leading-none"
            >
              {availableChestsCount}
            </TextXSmall>
          </div>
        )}
      </div>
    </div>
  );
};

export { ContractProgress };
